// libs
import { useState, useMemo, useEffect, useContext, useCallback } from "react";

// deps
import { getBridgeSdk, AnalyticsContext, useSessionStorage } from "@swiggy-private/checkout-core";
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";

// interface
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { CredEligibleBridgeData } from "@swiggy-private/payment-ui";

// helpers
import { isCredEligiblityValid } from "@payment-client/helpers/CredHelper";

//constants
import { CRED_ELIGIBLITY_TIMEOUT_E2 } from "@payment-client/constants/Cred";

//context
import { checkEligibilityStatusEvent } from "@payment-client/analytics/events/Cred";
import { useMountedRef } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

interface CredEligibleState {
    isEligible: boolean;
    bannerText: string;
}

const getCredEligibilityBannerTextResult = (
    withSplitPay: boolean,
    stateWithSplitPay: CredEligibleState | null,
    stateWithoutSplitPay: CredEligibleState | null,
): string => {
    if (withSplitPay && stateWithSplitPay !== null) {
        return stateWithSplitPay.bannerText;
    }

    if (!withSplitPay && stateWithoutSplitPay !== null) {
        return stateWithoutSplitPay.bannerText;
    }
    return "";
};

/*
Hook fetches cred eligiblity when page loads and
when there is a change is splitpay toggle
*/
const useCredEligible = ({
    paymentResponse,
    clientAuthToken,
    isSdkAvailable,
    withSplitPay,
    amountToPay,
}: {
    paymentResponse: GetPaymentMethodsResponse;
    clientAuthToken: string | undefined;
    isSdkAvailable: boolean;
    withSplitPay: boolean;
    amountToPay: string;
}): CredEligibleBridgeData => {
    //State
    const [eligibleCallInProgress, setEligibleCallInProgress] = useState(false);

    const [state, setState] = useSessionStorage<{ isEligible: boolean; bannerText: string } | null>(
        "cred_eligibility_data",
        null,
    );
    const [stateWithSplitPay, setStateWithSplitPay] = useSessionStorage<{
        isEligible: boolean;
        bannerText: string;
    } | null>("cred_eligibility_data_with_splitpay", null);

    //Refs
    const isMountedRef = useMountedRef();

    const { impressionEvent } = useContext(AnalyticsContext);

    const canUseCredPay = useMemo(() => {
        return isCredEligiblityValid(isSdkAvailable, clientAuthToken, paymentResponse.paymentGroup);
    }, [isSdkAvailable, paymentResponse.paymentGroup, clientAuthToken]);

    const bannerText = useMemo(() => {
        return getCredEligibilityBannerTextResult(withSplitPay, stateWithSplitPay, state);
    }, [state, stateWithSplitPay, withSplitPay]);

    const getCredEligibilityDetails = useCallback(
        async (amountToPay: string, withSplitPay: boolean) => {
            if (!isMountedRef.current) {
                return;
            }

            setEligibleCallInProgress(true);

            getBridgeSdk()
                .getCredEligibility(
                    {
                        clientAuthToken: clientAuthToken || "",
                        merchantId: paymentResponse.juspayMeta?.merchantId,
                        amount: amountToPay,
                        mobileNumber: "",
                        ...(!isEmpty(paymentResponse.tenant) && { tenant: paymentResponse.tenant as Tenant }),
                    },
                    CRED_ELIGIBLITY_TIMEOUT_E2,
                )
                .catch(() => {
                    setEligibleCallInProgress(false);
                })
                .then((data) => {
                    if (data != null) {
                        const setStateFn = withSplitPay ? setStateWithSplitPay : setState;

                        setStateFn({
                            isEligible: data?.isEligible || false,
                            bannerText: data?.bannerText || "",
                        });
                        setEligibleCallInProgress(false);
                    }
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clientAuthToken, isMountedRef, paymentResponse.juspayMeta],
    );

    useEffect(() => {
        if (!isMountedRef.current || !canUseCredPay) {
            return;
        }

        if ((state == null && !withSplitPay) || (stateWithSplitPay == null && withSplitPay)) {
            checkEligibilityStatusEvent(impressionEvent);
            getCredEligibilityDetails(amountToPay, withSplitPay);
        }
    }, [
        amountToPay,
        canUseCredPay,
        getCredEligibilityDetails,
        impressionEvent,
        isMountedRef,
        state,
        stateWithSplitPay,
        withSplitPay,
    ]);

    return {
        isEligible: state?.isEligible || false,
        bannerText,
        isOfferLoading: eligibleCallInProgress,
    };
};

export default useCredEligible;
